<template>
  <div>
    <!-- Formularz dodawania / edycji -->
    <section class="rounded-xl bg-white ring-1 ring-gray-900 ring-opacity-5 shadow-sm px-5 py-4 mb-6">
      <h2 class="text-xs font-medium uppercase tracking-wide text-gray-500">
        {{ form.id ? 'Edytuj kontrahenta' : 'Dodaj kontrahenta' }}
      </h2>
      <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label for="nazwa" class="block text-xs font-medium text-gray-600">Nazwa</label>
          <input id="nazwa" v-model="form.nazwa" type="text" placeholder="Agata Meble"
            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
        </div>
        <div>
          <label for="symbol" class="block text-xs font-medium text-gray-600">Symbol w Subiekcie</label>
          <input id="symbol" v-model="form.symbol" type="text" maxlength="20" placeholder="AGATA"
            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm uppercase focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
        </div>
        <div>
          <label for="aliasy" class="block text-xs font-medium text-gray-600">Aliasy (po przecinku)</label>
          <input id="aliasy" v-model="form.aliasyText" type="text" placeholder="agata, agata-meble"
            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
        </div>
      </div>
      <div class="mt-4 flex flex-wrap gap-2">
        <button @click="save()" :disabled="saving || !canSave" type="button"
          :class="[(saving || !canSave) ? 'cursor-not-allowed opacity-50' : 'hover:bg-indigo-500',
                   'inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200']">
          <RefreshIcon v-if="saving" class="h-4 w-4 animate-spin" aria-hidden="true" />
          {{ form.id ? 'Zapisz zmiany' : 'Dodaj' }}
        </button>
        <button v-if="form.id" @click="resetForm()" type="button"
          class="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition-all duration-200 hover:bg-gray-50">
          Anuluj
        </button>
      </div>
    </section>

    <div v-if="loading" class="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <RefreshIcon class="h-8 w-8 animate-spin text-indigo-500" aria-hidden="true" />
      <p class="text-sm text-gray-500">Wczytuję kartotekę…</p>
    </div>

    <div v-else-if="contractors.length === 0"
      class="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 py-12 text-center">
      <UsersIcon class="h-8 w-8 text-gray-300" aria-hidden="true" />
      <p class="text-sm font-medium text-gray-900">Kartoteka jest pusta</p>
      <p class="max-w-sm text-sm text-gray-500">
        Dopóki nie ma tu żadnego wpisu, system nie zaproponuje kontrahenta na żadnym dokumencie.
      </p>
    </div>

    <table v-else class="w-full text-sm">
      <thead>
        <tr class="border-b border-gray-200">
          <th class="py-2 pr-3 text-left font-medium text-gray-500">Nazwa</th>
          <th class="py-2 pr-3 text-left font-medium text-gray-500">Symbol</th>
          <th class="py-2 pr-3 text-left font-medium text-gray-500">Aliasy</th>
          <th class="py-2 text-right font-medium text-gray-500">Akcje</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in contractors" :key="item.id" class="border-b border-gray-100">
          <td class="py-2 pr-3 align-top text-gray-900">{{ item.nazwa }}</td>
          <td class="py-2 pr-3 align-top">
            <span class="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">{{ item.symbol }}</span>
          </td>
          <td class="py-2 pr-3 align-top text-gray-500">
            {{ (item.aliasy && item.aliasy.length > 0) ? item.aliasy.join(', ') : '—' }}
          </td>
          <td class="py-2 text-right align-top whitespace-nowrap">
            <button @click="edit(item)" type="button"
              class="text-xs text-indigo-600 transition duration-200 hover:text-indigo-500">Edytuj</button>
            <button @click="remove(item)" type="button"
              class="ml-3 text-xs text-red-600 transition duration-200 hover:text-red-500">Usuń</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { subiektPost } from '@/subiektApi';
import { RefreshIcon, UsersIcon } from '@heroicons/vue/solid';

export default {
  name: 'SubiektContractorsPanel',
  components: { RefreshIcon, UsersIcon },
  data() {
    return {
      loading: true,
      saving: false,
      contractors: [],
      form: { id: null, nazwa: '', symbol: '', aliasyText: '' },
    };
  },
  computed: {
    canSave() {
      return this.form.nazwa.trim().length > 0 && this.form.symbol.trim().length > 0;
    },
  },
  methods: {
    resetForm() {
      this.form = { id: null, nazwa: '', symbol: '', aliasyText: '' };
    },
    edit(item) {
      this.form = {
        id: item.id,
        nazwa: item.nazwa || '',
        symbol: item.symbol || '',
        aliasyText: (item.aliasy || []).join(', '),
      };
    },
    async load() {
      this.loading = true;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/kontrahenci', {});
        if(!res.data.success) throw res.data.log || res.data.error;
        this.contractors = res.data.kontrahenci || [];
      } catch (error) {
        console.log(error);
        this.$store.commit('setNotification', {
          show: true, head: 'Nie udało się wczytać kartoteki.',
          subheader: typeof error === 'string' ? error : 'Coś poszło nie tak.', success: false,
        });
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if(this.saving || !this.canSave) return;
      this.saving = true;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/kontrahenci/save', {
          id: this.form.id,
          nazwa: this.form.nazwa.trim(),
          symbol: this.form.symbol.trim(),
          aliasy: this.form.aliasyText.split(',').map(a => a.trim()).filter(a => a.length > 0),
          user: this.$store.state.userData,
        });
        if(!res.data.success) throw res.data.log || res.data.error;

        this.$store.commit('setNotification', {
          show: true, head: 'Zapisano.', subheader: res.data.log, success: true,
        });
        this.resetForm();
        await this.load();
      } catch (error) {
        console.log(error);
        this.$store.commit('setNotification', {
          show: true, head: 'Nie udało się zapisać.',
          subheader: typeof error === 'string' ? error : 'Coś poszło nie tak.', success: false,
        });
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/kontrahenci/delete', { id: item.id });
        if(!res.data.success) throw res.data.log || res.data.error;
        this.$store.commit('setNotification', {
          show: true, head: 'Usunięto.', subheader: res.data.log, success: true,
        });
        await this.load();
      } catch (error) {
        console.log(error);
        this.$store.commit('setNotification', {
          show: true, head: 'Nie udało się usunąć.',
          subheader: typeof error === 'string' ? error : 'Coś poszło nie tak.', success: false,
        });
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>
